import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pin, PinOff, Loader2 } from "lucide-react";
import { format, addDays, differenceInCalendarDays, isBefore, startOfDay } from "date-fns";
import { fr } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// "À la une": pins one article as the front-page lead of a native site.
// Stored as websites.theme_config.pinned = { slug, until }. The static site
// generator moves that article to the head of the list (hero / mosaic big
// tile / classic lead) and makes the homepage share card (og:title,
// og:description, og:image) that article. The pin expires on its own once
// `until` has passed. Saving also stamps render_requested_at so the render
// worker rebuilds the site on its next tour.

type Pinned = { slug: string; until?: string | null };
type PinArticle = { id: string; title: string; slug: string; published_at: string | null };

const db = supabase as any;
const isoDay = (d: Date) => format(d, "yyyy-MM-dd");

export default function FrontPagePinCard({ siteId }: { siteId: string }) {
  const queryClient = useQueryClient();
  const [slug, setSlug] = useState("");
  const [until, setUntil] = useState(() => isoDay(addDays(new Date(), 30)));

  const { data: site, isLoading } = useQuery({
    queryKey: ["website-pin", siteId],
    queryFn: async () => {
      const { data, error } = await db
        .from("websites")
        .select("id, domain, theme_config")
        .eq("id", siteId)
        .single();
      if (error) throw error;
      return data as { id: string; domain: string | null; theme_config: Record<string, any> | null };
    },
    enabled: !!siteId,
  });

  const { data: articles = [] } = useQuery({
    queryKey: ["website-pin-articles", siteId],
    queryFn: async () => {
      const { data, error } = await db
        .from("articles")
        .select("id, title, slug, published_at")
        .eq("website_id", siteId)
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(300);
      if (error) throw error;
      return data as PinArticle[];
    },
    enabled: !!siteId,
  });

  const themeConfig = site?.theme_config || {};
  const isNative = themeConfig.render_mode === "native";
  const current: Pinned | null = themeConfig.pinned?.slug ? themeConfig.pinned : null;
  const currentArticle = useMemo(
    () => (current ? articles.find((a) => a.slug === current.slug) : undefined),
    [articles, current],
  );
  const currentExpired = !!current?.until && isBefore(new Date(current.until), startOfDay(new Date()));
  const daysLeft = current?.until ? differenceInCalendarDays(new Date(current.until), new Date()) : null;

  // Prefill the form from the stored pin the first time the site loads.
  useEffect(() => {
    if (!site) return;
    if (current?.slug) setSlug(current.slug);
    if (current?.until) setUntil(current.until.slice(0, 10));
  }, [site]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: async (pinned: Pinned | null) => {
      const next = { ...themeConfig };
      if (pinned) next.pinned = pinned; else delete next.pinned;
      const updates: Record<string, unknown> = { theme_config: next };
      if (isNative) updates.render_requested_at = new Date().toISOString();
      const { error } = await db.from("websites").update(updates).eq("id", siteId);
      if (error) throw error;
      return pinned;
    },
    onSuccess: (pinned) => {
      queryClient.invalidateQueries({ queryKey: ["website-pin", siteId] });
      queryClient.invalidateQueries({ queryKey: ["website-detail", siteId] });
      queryClient.invalidateQueries({ queryKey: ["website-detail-page"] });
      queryClient.invalidateQueries({ queryKey: ["websites"] });
      toast.success(
        pinned ? "Article épinglé à la une" : "Article désépinglé",
        { description: isNative ? "Le site sera reconstruit dans les 30 minutes." : undefined },
      );
    },
    onError: (err: any) => toast.error(err.message || "Enregistrement impossible"),
  });

  const onPin = () => {
    if (!slug) return toast.error("Choisissez un article");
    if (!until) return toast.error("Choisissez une date de fin");
    save.mutate({ slug, until });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Pin className="h-4 w-4" /> À la une
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          L'article épinglé occupe la grande image de la page d'accueil et devient l'aperçu
          du site quand l'accueil est partagé sur les réseaux sociaux, jusqu'à la date choisie.
        </p>
        {!isNative && !isLoading && (
          <p className="text-xs text-amber-500">
            Disponible uniquement pour les sites rendus par Delapresse (mode natif).
          </p>
        )}

        {current && (
          <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-accent/30 p-3">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{currentArticle?.title || current.slug}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {current.until
                  ? currentExpired
                    ? `Expiré le ${format(new Date(current.until), "d MMMM yyyy", { locale: fr })}`
                    : `Jusqu'au ${format(new Date(current.until), "d MMMM yyyy", { locale: fr })}` +
                      (daysLeft !== null ? ` (${daysLeft} j)` : "")
                  : "Sans date de fin"}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant="outline" className={currentExpired ? "text-muted-foreground" : "border-primary/30 text-primary"}>
                {currentExpired ? "expiré" : "épinglé"}
              </Badge>
              <Button size="sm" variant="ghost" onClick={() => save.mutate(null)} disabled={save.isPending} title="Désépingler">
                <PinOff className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <div className="grid gap-3 md:grid-cols-[1fr_170px_auto] md:items-end">
          <div className="space-y-1.5 min-w-0">
            <Label className="text-xs">Article</Label>
            <Select value={slug} onValueChange={setSlug}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder={articles.length ? "Choisir un article publié" : "Aucun article publié"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {articles.map((a) => (
                  <SelectItem key={a.id} value={a.slug} className="text-sm">
                    <span className="truncate">{a.title}</span>
                    {a.published_at && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {format(new Date(a.published_at), "d MMM", { locale: fr })}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Jusqu'au</Label>
            <Input type="date" className="h-9 text-sm" value={until} min={isoDay(new Date())}
                   onChange={(e) => setUntil(e.target.value)} />
          </div>
          <Button size="sm" className="h-9" onClick={onPin} disabled={save.isPending || !slug || !articles.length}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pin className="h-4 w-4" />}
            <span className="ml-1.5">{current && current.slug === slug ? "Mettre à jour" : "Épingler"}</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
